import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {Apollo, gql} from 'apollo-angular';
import {Subject, Subscription} from 'rxjs';
import {
  NewClient_addClient,
  NewClient_addClientVariables,
} from './__generated__/NewClient_addClient';
import {ToastrService} from 'ngx-toastr';
import {CountryISO} from 'ngx-intl-tel-input';
import {UserMutationInput} from '../shared/graphql/globalTypes';
import {catchError, exhaustMap, withLatestFrom, map} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {phoneNumberValidator} from '../shared/utils';
import {NewClient_getUserId} from './__generated__/NewClient_getUserId';
import {ErrorService} from '../shared/services/error.service';

export enum Language {
  NL = 'NL',
  // EN = 'ENGLISH',
  DE = 'DE',
}

const GET_USER_ID = gql`
  query NewClient_getUserId {
    me {
      id
    }
  }
`;

const ADD_CLIENT = gql`
  mutation NewClient_addClient($input: UserMutationInput!) {
    createOrUpdateUser(input: $input) {
      user {
        id
        firstName
        username
        lastName
        language
      }
      errors {
        field
        messages
      }
    }
  }
`;

@Component({
  selector: 'app-new-client',
  templateUrl: './new-client.component.html',
  styleUrls: ['./new-client.component.scss'],
})
export class NewClientComponent implements OnInit, OnDestroy {
  invalidSubmission = false;
  subscriptions: Subscription[] = [];
  signupForm = new FormGroup({
    username: new FormControl(null, [Validators.required, phoneNumberValidator.bind(this)]),
    firstName: new FormControl(null, Validators.required),
    lastName: new FormControl(null, Validators.required),
    language: new FormControl(null, Validators.required),
    movementsEnabled: new FormControl(true, Validators.required),
  });
  usernameErrorMessages: string[] = [];
  firstNameErrorMessages: string[] = [];
  lastNameErrorMessages: string[] = [];
  CountryISO = CountryISO;

  submit$ = new Subject<UserMutationInput>();

  languages: Language[] = [
    Language.NL,
    // Language.EN,
    Language.DE,
  ];

  constructor(
    private apollo: Apollo,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
    private errorHandler: ErrorService,
  ) {}

  ngOnInit(): void {
    const languageTranslationMap: {[key in Language]: string} = {
      [Language.NL]: this.translate.instant('NL', Language.NL),
      // [Language.EN]: this.translate.instant('ENGLISH', Language.EN),
      [Language.DE]: this.translate.instant('DE', Language.DE),
    };

    this.languages.forEach(lang => languageTranslationMap[lang]);

    if (localStorage.getItem('language') === 'nl') {
      this.signupForm.controls['language'].setValue(Language.NL);
    } else if (localStorage.getItem('language') === 'de') {
      this.signupForm.controls['language'].setValue(Language.DE);
    }

    this.subscriptions.push(
      this.submit$
        .pipe(
          withLatestFrom(
            this.apollo
              .query<NewClient_getUserId>({
                query: GET_USER_ID,
              })
              .pipe(
                catchError(error => this.errorHandler.handleApollo(error)),
                map(resp => resp.data.me.id),
              ),
          ),
          exhaustMap(([input, userId]) => {
            return this.apollo
              .mutate<NewClient_addClient, NewClient_addClientVariables>({
                mutation: ADD_CLIENT,
                variables: {
                  input: input,
                },
                update: cache => {
                  cache.evict({id: 'ROOT_QUERY', fieldName: 'clients'});
                  cache.evict({id: `UserType:${userId}`, fieldName: 'chatRooms'});
                },
              })
              .pipe(catchError(error => this.errorHandler.handleApollo(error)));
          }),
        )
        .subscribe(result => {
          this.usernameErrorMessages = [];
          this.firstNameErrorMessages = [];
          this.lastNameErrorMessages = [];
          const errors = result.data?.createOrUpdateUser.errors;
          if (errors && errors.length > 0) {
            errors.forEach(e => {
              switch (e?.field) {
                case 'username':
                  this.usernameErrorMessages = e?.messages;
                  break;
                case 'firstName':
                  this.firstNameErrorMessages = e?.messages;
                  break;
                case 'lastName':
                  this.lastNameErrorMessages = e?.messages;
                  break;
                default:
                  break;
              }
            });
            return;
          }

          this.toastr.success(this.translate.instant('CLIENT_ADDED'), '', {
            timeOut: 2000,
          });
          this.router.navigate(['dashboard', 'clients']);
        }),
    );
  }

  onSubmit(): void {
    if (this.signupForm.invalid) {
      this.invalidSubmission = true;
      return;
    }

    this.signupForm.controls['language'].setValue(
      this.signupForm.controls['language'].value.toLowerCase(),
    );
    const value = this.signupForm.value;
    value.username = value.username.e164Number;
    this.submit$.next(value);
    this.signupForm.controls['language'].setValue(
      this.signupForm.controls['language'].value.toUpperCase(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
